import { ListItemText, Tooltip } from '@mui/material';
import { alpha, styled, useTheme } from '@mui/material/styles';
import classNames from 'classnames';
import { Fragment, useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { VerticalDivider } from 'src/components/VerticalDivider/VerticalDivider';
import Iconify from 'src/components/iconify';
import { useLocales } from 'src/locales';
import { Rule } from 'src/requests/roles.requests';
import {
  Section as RouterSessionKey,
  isRouterSessionKey,
  useCurrentSessionSectionsRecord,
} from 'src/state';

type HeaderButton = {
  title: string;
  routerKey: RouterSessionKey;
  path: string;
  icon: React.ReactNode;
  rule: Rule;
};

const NavButton = styled(Link)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(1),
  [theme.breakpoints.up('sm')]: {
    padding: theme.spacing(1, 1.5),
  },
  borderRadius: theme.shape.borderRadius,
  cursor: 'pointer',
  gap: theme.spacing(1),
  textDecoration: 'none',
  color: theme.palette.primary.contrastText,
  opacity: 0.5,
  '&.active': {
    backgroundColor: alpha(theme.palette.primary.contrastText, 0.24),
    color: theme.palette.primary.contrastText,
    opacity: 1,
    cursor: 'default',
  },
  '&:hover:not(.active)': {
    backgroundColor: alpha(
      theme.palette.primary.contrastText,
      theme.palette.action.activatedOpacity,
    ),
    color: theme.palette.primary.contrastText,
    opacity: 1,
  },
  '& .MuiListItemText-root': {
    display: 'none',
    [theme.breakpoints.up('lg')]: {
      display: 'block',
    },
  },
}));

export const NavButtons = () => {
  const sessionRoutes = useCurrentSessionSectionsRecord();

  const location = useLocation();
  const { translate } = useLocales();

  const theme = useTheme();

  const headerButtons = useMemo(() => {
    return [
      {
        title: translate('header.communicate'),
        routerKey: 'communicate',
        path: sessionRoutes.communicate,
        icon: <Iconify icon="tabler:message" />,
        rule: Rule.MOCK_ALLOW,
      },
      {
        title: translate('header.collaborate'),
        routerKey: 'collaborate',
        path: sessionRoutes.collaborate,
        icon: <Iconify icon="tabler:clipboard-text" />,
        rule: Rule.MOCK_ALLOW,
      },
      {
        title: translate('header.ai'),
        routerKey: 'ai',
        path: sessionRoutes.ai,
        icon: <Iconify icon="copera:ai" />,
        rule: Rule.MOCK_ALLOW,
      },
    ] as HeaderButton[];
  }, [sessionRoutes, translate]);

  return (
    <>
      {headerButtons.map((button, index) => (
        <Fragment key={button.title}>
          {!!index && (
            <VerticalDivider
              height={20}
              color={alpha(theme.palette.common.black, 0.12)}
            />
          )}
          <Tooltip title={translate(button.title)} placement="bottom" arrow>
            <NavButton
              className={classNames({
                active: isRouterSessionKey(button.routerKey, location.pathname),
              })}
              to={button.path}
              key={button.title}
            >
              {button.icon}

              <ListItemText
                primary={`${translate(button.title)}`}
                primaryTypographyProps={{
                  typography: 'body2',
                  fontWeight: 500,
                  whiteSpace: 'nowrap',
                }}
              />
            </NavButton>
          </Tooltip>
        </Fragment>
      ))}
    </>
  );
};
